import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import PaymentIcon from '@mui/icons-material/Payment';
import WatchLaterRoundedIcon from '@mui/icons-material/WatchLaterRounded';
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import { FC, Fragment, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { ITicketPostViewModel } from 'Interfaces/ITicketConfig';
import { FormBoxElevated } from 'components/form';
import ratesAtom from 'data/atoms/ratesAtom';
import userAtom from 'data/atoms/userAtom';
import { BillableTime } from 'functions/BillableTime';
import GetChartColor from 'functions/GetChartColor';
import { getTimeDifference } from 'functions/GetTimeDifference';
import { TotalBillableAmount } from 'functions/TotalBillableAmount';
import { calculateTicketPostBillingDetails } from 'functions/calculateTicketPostBillingDetails';
import { rq, rqPost } from 'http/apiRoutes';
import { useGet, usePut } from 'http/useApi';
import { useParams } from 'react-router';
import { initialRootState, RootReducer } from './Components/RotTicketReducer';
import { ConvertStateToTicketUpdateWithProductViewModel } from './TicketDetails';

export const TicketEconomy: FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(RootReducer, initialRootState);
  const { ticketId } = useParams(); // check if companyId is valid?

  const { height } = useWindowsDimension();
  const [user] = useAtom(userAtom);
  const [rates] = useAtom(ratesAtom);
  let isEnabled = ticketId !== 'new' ? true : true;
  const { data: ticketData } = useGet(
    rq.ticket(ticketId || '', isEnabled, '0'),
    
  );
  isEnabled = state.ticket.id !== 'new' ? true : true;
  const putTicket = usePut(
    rqPost.ticketPut(state.ticket.id || '', '0', isEnabled)
  );

  useEffect(() => {
    if (ticketData) {
      console.log('TICKET DATA =>', ticketData);
      dispatch({ type: 'SET_TICKET', payload: ticketData });
    }
  }, [ticketData]);

  const FixedHourlyRateJSX = () => (
    <TextField
      label={t('Ticket.TicketEconomy.fixedHourlyRate')}
      defaultValue={
        state.ticket.fixedHourlyRate === -1 ? '' : state.ticket.fixedHourlyRate
      }
      onBlur={(e) =>
        dispatch({
          type: 'UPDATE_TICKET_FIELD',
          payload: {
            field: 'fixedHourlyRate',
            value: Number(e.target.value),
          },
        })
      }
      InputProps={{ endAdornment: <span>kr</span> }}
      variant='outlined'
      style={{ maxWidth: 250 }}
    />
  );

  const FixedPriceJSX = () => (
    <TextField
      label={t('Ticket.TicketEconomy.fixedPrice')}
      defaultValue={
        state.ticket.fixedPrice === -1 ? '' : state.ticket.fixedPrice
      }
      onBlur={(e) =>
        dispatch({
          type: 'UPDATE_TICKET_FIELD',
          payload: {
            field: 'fixedPrice',
            value: Number(e.target.value),
          },
        })
      }
      InputProps={{ endAdornment: <span>kr</span> }}
      variant='outlined'
      style={{ maxWidth: 250 }}
      sx={{ textAlign: 'left' }}
    />
  );

  const MaxPriceJSX = () => (
    <TextField
      label={t('Ticket.TicketEconomy.maxPrice')}
      defaultValue={state.ticket.maxPrice === -1 ? '' : state.ticket.maxPrice}
      onBlur={(e) =>
        dispatch({
          type: 'UPDATE_TICKET_FIELD',
          payload: {
            field: 'maxPrice',
            value: Number(e.target.value),
          },
        })
      }
      InputProps={{ endAdornment: <span>kr</span> }}
      variant='outlined'
      style={{ maxWidth: 250 }}
    />
  );

  const onTicketSubmit = async () => {
    let newTicket = ConvertStateToTicketUpdateWithProductViewModel(
      state.ticket,
      user
    );
    console.log('NEWTICKET =>', newTicket);
    putTicket.mutate(newTicket, {
      onSuccess: (res: any) => {
        console.log('NEWTICKET => res', res);
        dispatch({ type: 'SET_TICKET', payload: res });
        //  navigate(`/tickets`);
      },
    });
  };

  if (!state.ticket) return null;

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
      <Grid item xs={6} sx={{ pr: 1 }}>
        <Paper
          variant='elevation3'
          sx={{ p: 2, height: Math.ceil(height - 128), borderRadius: 2 }}
        >
          <Grid item xs={12} sx={{ mb: 2 }}>
            <FormBoxElevated
              Icon={WatchLaterRoundedIcon}
              title={t('Ticket.TicketEconomy.fixedHourlyRate')}
              FormJSX={FixedHourlyRateJSX}
            />
          </Grid>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <FormBoxElevated
              Icon={MonetizationOnRoundedIcon}
              title={t('Ticket.TicketEconomy.fixedPrice')}
              FormJSX={FixedPriceJSX}
            />
          </Grid>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <FormBoxElevated
              Icon={PaymentIcon}
              title={t('Ticket.TicketEconomy.maxPrice')}
              FormJSX={MaxPriceJSX}
            />
          </Grid>
          <Box sx={{ ml: 2, width: '100%', display: 'flex' }}>
            <Button variant='contained' onClick={onTicketSubmit}>
              {t('Ticket.TicketEconomy.updateTicket')}
            </Button>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={6} sx={{ pl: 1 }}>
        <Paper
          variant='elevation3'
          sx={{
            height: 64,
            borderRadius: 2,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          {state.ticketPost.ticketPosts.length > 0 && (
            <Grid
              container
              sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}
            >
              <Typography
                sx={{
                  p: 2,
                  fontSize: 24,
                  letterSpacing: 3,
                  textAlign: 'right',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  flexGrow: 1,
                }}
              >
                {t('Ticket.TicketEconomy.total')}
              </Typography>
              <Grid sx={{ p: 2 }}>
                {state.ticket.fixedPrice > 0 ? (
                  <Typography
                    sx={{
                      m: 0,
                      width: 150,
                      fontSize: 24,
                      letterSpacing: 3,
                      textAlign: 'right',
                      color: 'secondary.text',
                      fontWeight: 'bold',
                    }}
                  >
                    {state.ticket.fixedPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                ) : (
                  <TotalBillableAmount
                    posts={state.ticketPost.ticketPosts}
                    size={24}
                    color='secondary.text'
                    fixedHourlyRate={state.ticket.fixedHourlyRate}
                    maxPrice={state.ticket.maxPrice}
                  />
                )}
              </Grid>
            </Grid>
          )}
        </Paper>
        <List
          sx={{
            pl: 1,
            pr: 1,
            height: Math.ceil(height - 258),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {state.ticketPost.ticketPosts.map(
            (post: ITicketPostViewModel, index) =>
              post.isBillable && post.direction === 1 && !post.internal ? (
                <ListItem
                  key={index}
                  alignItems='flex-start'
                  sx={{ p: 0, m: 0, borderRadius: 3 }}
                >
                  <ListItemText
                    primary={
                      <Grid
                        container
                        sx={{
                          p: 0,
                          m: 0,
                          borderRadius: 2,
                          width: '100%',
                          backgroundColor: `${GetChartColor(6)}.dark`,
                        }}
                      >
                        <Typography
                          sx={{
                            pl: 1,
                            display: 'inline',
                            fontSize: 18,
                            color: 'primary.text',
                          }}
                        >
                          {dayjs(post.startTime).format('DD/MM-YYYY HH:mm')}
                        </Typography>
                        <Typography
                          sx={{
                            pl: 3,
                            width: 75,
                            display: 'inline',
                            fontSize: 18,
                            color: 'warning.light',
                          }}
                        >
                          -
                          {getTimeDifference(
                            post.startDeduction,
                            post.endDeduction
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            pl: 3,
                            display: 'inline',
                            fontSize: 18,
                            color: 'primary.text',
                          }}
                        >
                          {dayjs(post.endTime).format('HH:mm')}
                        </Typography>
                        <Typography sx={{ flexGrow: 1 }} />
                        <HistoryToggleOffIcon />
                        <BillableTime
                          startTime={post.startTime}
                          startDeduction={post.startDeduction}
                          endDeduction={post.endDeduction}
                          endTime={post.endTime}
                          size={16}
                          color='secondary.text'
                        />
                        <AttachMoneyIcon />
                        <Typography
                          sx={{
                            display: 'inline',
                            fontSize: 16,
                            color: 'secondary.text',
                          }}
                        >
                          {calculateTicketPostBillingDetails(
                            state.ticket,
                            post,
                            rates
                          ).totalBillableValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Grid>
                    }
                    secondary={
                      <Fragment>
                        <Typography
                          component='span'
                          variant='body1'
                          color='text.primary'
                          sx={{ fontWeight: 'bold', display: 'inline' }}
                        >
                          {post.author?.name}{' '}
                        </Typography>
                        <Typography
                          component='span'
                          variant='body2'
                          color='text.primary'
                          sx={{ display: 'inline' }}
                        >
                          {post.message}
                        </Typography>
                      </Fragment>
                    }
                  />
                </ListItem>
              ) : null
          )}
        </List>
        <Paper
          variant='elevation3'
          sx={{
            height: 64,
            borderRadius: 2,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>
    </Grid>
  );
};
